import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import DetailsListAbstract, {
    ICoreColumn,
    ICoreDetailsListFilter,
    ICoreDetailsListSettings,
} from '../../components/DetailsList/DetailsListAbstract'
import { Link as ReactLink } from '@fluentui/react-components'
import { IActionButton } from '../../components/ActionButtons'
import { IContextualMenuItem, ISearchBox, SearchBox, Stack, Toggle } from '@fluentui/react'
import { PartyAction, PartyOverviewItemDto } from '../../config/partyConfig'
import { outlookDisplayNewAppointmentForm, outlookDisplayNewMessageForm } from '../../helpers/outlookHelper'
import { regexSearchable } from '../../helpers/regexHelper'
import { Link } from 'react-router-dom'
import { PartyStatus, PartyType } from '../../components/Form/Party/PartyForm'
import ActionHeader from '../../components/Render/ActionHeader'
import ActionHeaderFilter from '../../components/Render/ActionHeaderFilter'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../reducers'
import { DataPromise, shouldDispatchLoad } from '../../context/AxiosContext'
import { searchFilter } from '../../helpers/detailsListHelper'
import { MenuItemKey } from '../../components/Menu'
import { AppAction } from '../../config/appConfig'
import { FileAction } from '../../config/fileConfig'
import useAxios from '../../hooks/useAxios'
import { getPartyIcon } from '../../helpers/iconHelper'
import { useTranslation } from 'react-i18next'
import useRoles, { Role } from '../../hooks/useRoles'
import CsvReportButton from '../../components/Button/CsvReportButton'
import { DocumentGenerationType } from '../../hooks/document/useDocumentGenerationHandler'
import { composeEmailClick } from '../../components/List/FileParty/FilePartyList'

type PartyListSettings = ICoreDetailsListSettings & {
    showNatural: boolean
    showLegal: boolean
    showAll: boolean
}

type Props = {
    settings?: Partial<PartyListSettings>
}

const getUpdateLink = (party: PartyOverviewItemDto): string => {
    switch (party.type) {
        case PartyType.Legal: {
            return `/party/legal/${party.id}/edit`
        }
        case PartyType.Natural:
        default: {
            return `/party/natural/${party.id}/edit`
        }
    }
}

const getActionButtonItems = (translate: (key: string) => string): IActionButton[] => [
    {
        key: 'add-natural-person',
        title: translate('action.create.person.natural.title'),
        secondaryText: translate('action.create.person.natural.subtitle'),
        iconName: 'Contact',
        route: '/party/natural/add',
    },
    {
        key: 'add-legal-person',
        title: translate('action.create.person.legal.title'),
        secondaryText: translate('action.create.person.legal.title'),
        iconName: 'BusinessCenterLogo',
        route: '/party/legal/add',
    },
]

const getContextualMenuItems = (translate: (key: string) => string): IContextualMenuItem[][] => [
    [
        {
            key: 'sendmail',
            iconProps: { iconName: 'OutlookLogo' },
            text: translate('action.create.email'),
            access: (item: PartyOverviewItemDto) => !!item.email,
            click: (item: PartyOverviewItemDto) =>
                outlookDisplayNewMessageForm({ toRecipients: item.email.split(/[,;]/) }),
        },
        {
            key: 'appointment',
            iconProps: { iconName: 'Calendar' },
            text: translate('action.create.appointment'),
            access: (item: PartyOverviewItemDto) => !!item.email,
            click: (item: PartyOverviewItemDto) =>
                outlookDisplayNewAppointmentForm({ requiredAttendees: item.email.split(/[,;]/) }),
        },
    ],
]

const getColumns = (translate: (key: string) => string): ICoreColumn[] => [
    {
        key: 'type',
        name: '',
        fieldName: '',
        minWidth: 24,
        maxWidth: 24,
        isResizable: true,
        isRowHeader: true,
        className: 'persona',
        onRender: (item: PartyOverviewItemDto) => getPartyIcon(item, { width: '24px', height: '24px' }),
    },
    {
        key: 'displayName',
        name: translate('base.table.header.name'),
        fieldName: 'displayName',
        minWidth: 300,
        isResizable: true,
        isRowHeader: true,
        isSorted: true,
        data: (item: PartyOverviewItemDto) => item.displayName && regexSearchable(item.displayName),
        onRender: (item: PartyOverviewItemDto) => (
            <Link
                key={item.id}
                to={getUpdateLink(item)}
                className={item.status === 'ACTIVE' ? 'party item-open' : 'party item-closed'}
            >
                {item.displayName || <em>Naam ontbreekt.</em>}
            </Link>
        ),
    },
    {
        key: 'email',
        name: translate('base.table.header.email'),
        fieldName: 'email',
        minWidth: 200,
        isResizable: true,
        isRowHeader: true,
        isCollapsible: true,
        data: (item: PartyOverviewItemDto) => item.email && regexSearchable(item.email),
        onRender: (item: PartyOverviewItemDto) => (
            <ReactLink id={item.id} onClick={composeEmailClick(item)}>
                {item.email}
            </ReactLink>
        ),
    },
    {
        key: 'phoneNumber',
        name: translate('base.table.header.phoneNumber'),
        fieldName: 'phoneNumber',
        minWidth: 120,
        isResizable: true,
        isCollapsible: true,
        isRowHeader: true,
        data: (item: PartyOverviewItemDto) => regexSearchable(item.phoneNumber),
    },
]

const getFilters = (columns: ICoreColumn[]): ICoreDetailsListFilter<PartyOverviewItemDto, PartyListSettings>[] => [
    (items: PartyOverviewItemDto[], settings: PartyListSettings) =>
        settings.showAll ? items : items.filter((item: PartyOverviewItemDto) => item.status === PartyStatus.Active),
    (items: PartyOverviewItemDto[], settings: PartyListSettings) =>
        !settings.showNatural || !settings.showLegal
            ? items.filter(
                  (item: PartyOverviewItemDto) =>
                      (settings.showNatural && item.type === PartyType.Natural) ||
                      (settings.showLegal && item.type === PartyType.Legal),
              )
            : items,
    searchFilter(columns),
]

export default function PartyOverview(props: Props): ReactElement {
    const dispatch = useDispatch()
    const roles = useRoles()
    const { t: translate } = useTranslation()

    const parties = useSelector<Store, DataPromise<PartyOverviewItemDto[]>>(state => state.parties.overview)
    const [showNatural, setShowNatural] = useState<boolean>(true)
    const [showLegal, setShowLegal] = useState<boolean>(true)
    const [showAll, setShowAll] = useState<boolean>(false)
    const [query, setQuery] = useState<string>('')
    const [matchColumns, setMatchColumns] = useState<string[]>(['displayName', 'phoneNumber'])
    const searchBoxRef = useRef<ISearchBox>()
    const axios = useAxios()

    const actionButtonItems = useMemo(() => getActionButtonItems(translate), [])
    const contextualMenuItems = useMemo(() => getContextualMenuItems(translate), [])
    const columns = useMemo(() => getColumns(translate), [])
    const filters = useMemo(() => getFilters(columns), [columns])

    useEffect(() => {
        setShowNatural(props.settings?.showNatural ?? showNatural)
        setShowLegal(props.settings?.showLegal ?? showLegal)
        setShowAll(props.settings?.showAll ?? showAll)
        setQuery(props.settings?.query ?? query)
        setMatchColumns(props.settings?.matchColumns ?? matchColumns)
    }, [props.settings])

    useEffect(() => {
        searchBoxRef.current?.focus()
    }, [searchBoxRef.current])

    useEffect(() => {
        dispatch({ type: AppAction.SetBreadcrumbs, payload: [MenuItemKey.Parties] })
        dispatch({ type: FileAction.ResetCurrent })
    }, [props])

    useEffect(() => {
        if (shouldDispatchLoad(parties)) {
            dispatch({
                type: PartyAction.GetOverview,
                payload: axios.get('/party'),
            })
        }
    }, [parties])

    return (
        <>
            <ActionHeader actionButtonItems={actionButtonItems}>
                <ActionHeaderFilter label={translate('party.filter.natural.title')}>
                    <Toggle
                        defaultChecked={showNatural}
                        onText={translate('base.toggle.on')}
                        offText={translate('base.toggle.off')}
                        onChange={(_, checked?: boolean) => setShowNatural(checked)}
                    />
                </ActionHeaderFilter>
                <ActionHeaderFilter label={translate('party.filter.legal.title')}>
                    <Toggle
                        defaultChecked={showLegal}
                        onText={translate('base.toggle.on')}
                        offText={translate('base.toggle.off')}
                        onChange={(_, checked?: boolean) => setShowLegal(checked)}
                    />
                </ActionHeaderFilter>
                <ActionHeaderFilter label={translate('party.filter.inactive.title')}>
                    <Toggle
                        defaultChecked={showAll}
                        onText={translate('base.toggle.on')}
                        offText={translate('base.toggle.off')}
                        onChange={(_, checked?: boolean) => setShowAll(checked)}
                    />
                </ActionHeaderFilter>
            </ActionHeader>
            <SearchBox
                className="clear"
                componentRef={searchBoxRef}
                placeholder={translate('party.filter.search')}
                underlined
                value={query}
                onChange={(_, query?: string) => setQuery(query)}
            />

            {roles.all([Role.Report]) && (
                <Stack horizontal>
                    <CsvReportButton<PartyListSettings>
                        generationType={DocumentGenerationType.PartyXls}
                        payload={() => {
                            return { showNatural, showLegal, showAll, query }
                        }}
                    />
                </Stack>
            )}
            <DetailsListAbstract
                columns={columns}
                items={parties.data}
                filters={filters}
                settings={{ showNatural, showLegal, showAll, query, matchColumns }}
                isSortable
                contextualMenuItems={contextualMenuItems}
                getContextualMenuTitle={(item: PartyOverviewItemDto) => item.displayName}
                emptyDescription={translate('base.table.empty')}
            />
        </>
    )
}
